import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';

import { STATISTIC_CONST } from '@/constant';
import { useTaskPeriodMetricQuery } from '@/hook';
import {
  VFlex,
  VStack,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
} from '@/package';
import type { Task, User } from '@/type';

import { calculatePeriodData } from '../Statistic';
import BarChart from '../Statistic/BarChart/BarChart';
import { RangePicker } from '../Statistic/RangePicker';
import type { PeriodUnit } from '../Statistic/type';

export const TaskPeriodLabeledObject = ({
  taskDetail,
  selectedMember,
}: {
  taskDetail: Task;
  selectedMember?: User['id'];
}) => {
  const [periodUnit, setPeriodUnit] = useState<PeriodUnit>('day');
  const [between, setBetween] = useState<[Dayjs, Dayjs]>([
    dayjs(STATISTIC_CONST.TODAY).subtract(12, 'week'),
    dayjs(STATISTIC_CONST.TODAY),
  ]);
  const { data: taskMetric } = useTaskPeriodMetricQuery({
    projectID: taskDetail.projectID,
    taskID: taskDetail.id,
    period: periodUnit,
    between,
    workerID: selectedMember,
  });

  const extendClassList = taskMetric?.extendClass?.filter(
    item => item.classType !== 'Unknown',
  );

  const periodData = calculatePeriodData(
    extendClassList,
    taskMetric?.userMetric,
  );

  return (
    <VStack gap={0.5} className="h-full pb-[100px]">
      <div className="text-headline-md font-bold text-secondary-500">
        Labeled objects statistics
      </div>

      <VFlex className="w-full" justify="between" items="center">
        <div className="text-headline-s font-semibold text-primary-600">
          Trend
        </div>
        <VFlex items="center" className="w-full" justify="end" gap={0.5}>
          <RangePicker
            value={between}
            onChange={setBetween}
            periodUnit={periodUnit}
            key={periodUnit}
          />

          <VTab
            defaultValue="day"
            onValueChange={val => setPeriodUnit(val as PeriodUnit)}
            size="small"
            value={periodUnit}
            variant="secondary"
          >
            <VTabTriggerContainer>
              <VTabTrigger value="day">Daily</VTabTrigger>
              <VTabTrigger value="week">Weekly</VTabTrigger>
              <VTabTrigger value="month">Monthly</VTabTrigger>
            </VTabTriggerContainer>
          </VTab>
        </VFlex>
      </VFlex>

      {periodData.length > 0 && (
        <BarChart data={periodData} periodUnit={periodUnit} between={between} />
      )}
    </VStack>
  );
};

TaskPeriodLabeledObject.displayName = 'TaskPeriodLabeledObject';
